import { each, isEmpty, isNil, isObject, isString, map } from 'lodash';

import { AlertType } from '@enums/alert';
import { showAlert } from '@lib/alert/alert-manager';
import { KnowledgeAttribute } from '@store/atoms/knowledge-attributes';
import { toDateFormat } from '@utils/date';

import { FilterData } from './components/brand-library/asset-table/AttributeFilter';
import {
  FILE_SIZE_EXCEEDED_MESSAGE,
  tiffFileExtensions,
  UNSUPPORTED_FILE_EXT_ERROR,
  videoExtensionsToMimeType,
} from './constants';
import { AssetType } from './enums';
import {
  AttributeType,
  DataItem,
  FileError,
  GroupedKnowledgeItems,
  RejectedFile,
} from './types';

export const doesValueExist = (value: string, searchTerm: string) => {
  return value?.toLowerCase().includes(searchTerm.toLowerCase());
};

export const searchObject = (obj: DataItem, searchTerm: string) => {
  for (const value of Object.values(obj)) {
    // Searching in nested objects
    if (isObject(value) && !isNil(value)) {
      if (searchObject(value as DataItem, searchTerm)) {
        return true;
      }
    } else if (isString(value) && doesValueExist(value, searchTerm)) {
      return true;
    }
  }

  return false;
};

export const filterResults = (searchTerm: string, assets: DataItem[]) => {
  if (searchTerm) {
    const filteredResults = assets?.filter((item: DataItem) =>
      searchObject(item, searchTerm),
    );

    return filteredResults;
  }

  return assets;
};

export const getTruncatedFontName = (fontName: string) => {
  return fontName ? fontName.match(/(.+?)(\.[^.]*$|$)/)?.[1] : '';
};

export const loadCustomFonts = async (customFonts: any[]) => {
  // remove extension from font file to create font family
  const customFontFaces = customFonts?.map(font => {
    const fontName = font?.name || font?.fileName || '';

    return {
      fontAsset: font,
      font: new FontFace(
        getTruncatedFontName(fontName) || '',
        `url(${font.thumbnailUrl})`,
      ),
    };
  });

  const loadedFontsPromise = Promise.all(
    map(customFontFaces, cff => {
      return cff.font.load().catch(error => {
        console.warn(`Error loading font: ${cff.font.family}`);

        return { error, font: cff.fontAsset };
      });
    }),
  );

  const loadedFonts = await loadedFontsPromise;
  const fontErrors: any = [];
  if (!isEmpty(loadedFonts)) {
    each(loadedFonts as any[], res => {
      if (res.error) {
        fontErrors.push(res.font);
      } else {
        (document as any).fonts.add(res);
      }
    });
  }
  await document.fonts.ready;

  return fontErrors;
};

export const convertKnowledgeOptionsToArray = (
  knowledgeOptions: KnowledgeAttribute[],
): AttributeType[] => {
  return knowledgeOptions.map(item => ({
    id: item.attributeName,
    label: item.displayName,
  }));
};

// Helper function to group document items
export const groupDocumentItems = (
  items: DataItem[],
  workspaceId: number,
): (DataItem | DataItem[])[] => {
  const groupedItems: (DataItem | DataItem[])[] = [];
  let tempGroup: DataItem[] = [];

  for (const item of items) {
    if (item.parentClientId === workspaceId) {
      if (tempGroup.length) {
        groupedItems.push(tempGroup);
        tempGroup = [];
      }
      groupedItems.push(item);
    } else {
      tempGroup.push(item);
    }
  }

  if (tempGroup.length) groupedItems.push(tempGroup);

  return groupedItems;
};

export const flattenItems = (items: (DataItem | DataItem[])[]) =>
  items.flatMap(item => (Array.isArray(item) ? item : [item]));

export const filterCombinedDocumentItems = (
  combinedItems: (DataItem | DataItem[])[],
  dataItems: DataItem[],
): (DataItem | DataItem[])[] => {
  const dataItemIds = new Set(dataItems.map(item => item.id));
  const filteredItems: (DataItem | DataItem[])[] = [];

  combinedItems.forEach(item => {
    if (Array.isArray(item)) {
      const filteredGroup = item.filter(groupedItem =>
        dataItemIds.has(groupedItem.id),
      );
      if (filteredGroup.length > 0) {
        filteredItems.push(filteredGroup);
      }
    } else if (dataItemIds.has(item.id)) {
      filteredItems.push(item);
    }
  });

  return filteredItems;
};

const filterByDocuments = (
  dataItems: DataItem[],
  selectedDocuments: FilterData[],
) =>
  selectedDocuments.length > 0
    ? dataItems.filter(dataItem =>
        selectedDocuments.some(doc => doc.id === dataItem?.sourceDocument?.id),
      )
    : dataItems;

export const getFilteredKnowledgeItems = (
  knowledgeItems: GroupedKnowledgeItems,
  {
    category: selectedCategories,
    document: selectedDocuments,
  }: {
    category: FilterData[];
    document: FilterData[];
  },
): GroupedKnowledgeItems | null => {
  if (selectedCategories.length === 0 && selectedDocuments.length === 0) {
    return null;
  }

  const filteredItems = Object.keys(knowledgeItems || {}).reduce(
    (result, category) => {
      const isCategorySelected = selectedCategories.some(
        option => option.label === category,
      );

      const dataItems = knowledgeItems?.[category] || [];
      let matchingDataItems: DataItem[] = [];

      if (selectedCategories.length && selectedDocuments.length) {
        // Both category and document filters are present
        if (isCategorySelected) {
          matchingDataItems = filterByDocuments(dataItems, selectedDocuments);
          if (matchingDataItems.length) {
            result[category] = matchingDataItems;
          }
        }
      } else if (selectedCategories.length && isCategorySelected) {
        // Only category filter is present
        matchingDataItems = dataItems;
        result[category] = matchingDataItems;
      } else if (selectedDocuments.length) {
        // Only document filter is present
        matchingDataItems = filterByDocuments(dataItems, selectedDocuments);
        if (matchingDataItems.length) {
          result[category] = matchingDataItems;
        }
      }

      return result;
    },
    {} as GroupedKnowledgeItems,
  );

  return filteredItems;
};

export const isValidImage = (url: string) => {
  const extension = url.split('.').pop()?.toLowerCase();

  return extension && !tiffFileExtensions.includes(extension);
};

export function handleFileRejection(
  fileRejections: RejectedFile[],
  onFilesReject?: (files: { file: File; errors: FileError[] }[]) => void,
) {
  const rejectedFiles = fileRejections.map(rejection => ({
    file: rejection.file,
    errors: rejection.errors,
  }));

  if (onFilesReject) {
    onFilesReject(rejectedFiles);

    return;
  }

  const hasInvalidType = rejectedFiles.some(file =>
    file.errors.some(error => error.code === 'file-invalid-type'),
  );
  const hasExceededSize = rejectedFiles.some(file =>
    file.errors.some(error => error.code === 'file-too-large'),
  );

  if (hasInvalidType) {
    showAlert({
      type: AlertType.ERROR,
      header: 'Invalid File Type',
      message: UNSUPPORTED_FILE_EXT_ERROR,
    });

    return;
  }

  if (hasExceededSize) {
    showAlert({
      type: AlertType.ERROR,
      header: 'File Size Limit Exceeded',
      message: FILE_SIZE_EXCEEDED_MESSAGE,
    });
  }
}

export const getVideoMimeTypeFromUrl = (url: string): string => {
  const extension = url.split('.').pop()?.toLowerCase() || '';

  return videoExtensionsToMimeType[extension] || 'video/mp4';
};

// Helper function to create base properties for all asset types
export const createBaseProperties = (item: any) => ({
  id: item.id,
  name: item?.name,
  createdBy: item.userName,
  createdAt: toDateFormat(item.createdAt),
  parentClientId: item.parentClient?.id,
  parentClientName: item.parentClient?.name,
  isLocked: item.isLocked ? item.isLocked : false,
  externalAssetMetadata: item?.externalAssetMetadata,
  labels: item.labels,
});

// Helper function to create URL properties
export const createUrlProperties = (item: any) => ({
  url: item?.previewUrl || item?.url || '',
  thumbnailUrl: item?.thumbnailUrl || '',
});

// Helper function to sort items by hierarchy
export const sortByHierarchy = <T extends DataItem>(
  items: T[],
  hierarchy: number[],
  setList?: (list: T[]) => void,
): T[] => {
  if (hierarchy.length === 0) {
    if (setList) setList(items);

    return items;
  }

  const itemMap: Record<number, T> = items.reduce(
    (acc: Record<number, T>, item: T) => {
      acc[item.id] = item;

      return acc;
    },
    {},
  );

  const sortedList = hierarchy.map(id => itemMap[id]).filter(Boolean);

  if (setList) setList(sortedList);

  return sortedList;
};

// Helper function to filter and map assets by type
export const transformAssetsByType = <T extends DataItem>(
  brandAssets: any[],
  assetType: AssetType,
  mapper: (item: any) => T,
): T[] => {
  return brandAssets.filter(item => item.assetType === assetType).map(mapper);
};
